import { useCallback, useEffect, useState } from "react";
import { updateBillingDetailsById } from "../../PayrollService";
import Swal from "sweetalert2";
import { generatePayrollBilling } from "./PayrollBillingService";
import BillingInformationService from "domain/billing/BillingInformationService";

import { format, parse } from 'date-fns'
import AllowanceService from "domain/billing/AllowanceService";
import BillingFigureCalculatorService from "domain/billing/BillingFigureCalculatorService";
import BillingUtilities from "domain/billing/BillingUtilities";
import { useParams } from "react-router-dom";
import ColumnsGeneratorService from "domain/billing/ColumnsGeneratorService";
import {
    buildOtherDeductionColumns,
    flattenOtherDeductionBreakdown,
} from "utils/otherDeductionColumns";



/**
 * billing details object
 * 
 * [
    "finance_director",
    "president",
    "amount_in_words",
    "_id",
    "payroll_id",
    "cutoff",
    "payroll_from",
    "payroll_to",
    "company",
    "store",
    "gross_billing",
    "admin_cost",
    "rebate",
    "soa_number",
    "vat",
    "withholding_tax",
    "salary_deduction",
    "meal_allowance",
    "other_deductions",
    "payroll_analyst",
    "payroll_head",
    "payroll_manager",
    "billed_to",
    "qa_analyst",
    "date_settlement",
    "payroll_officer",
    "director_finance_dept",
    "createdAt",
    "updatedAt",
    "__v",
    "id"
]
 * 

    
payroll fields

    [
    "fileName",
    "store",
    "payroll_from",
    "payroll_to",
    "cutoff",
    "generated_by",
    "generated_by_name",
    "date_generated",
    "allowances",
    "informations",
    "columns" // to be generated 
    "user"
]


   //three allowance type: billing, client, gross_billing

  A
 * 
 */


//payroll billing aggregate
const usePayroll = () => {
    let { id } = useParams()


    let [payrollAndBilling, setPayrollAndBilling] = useState({
        status: '',
        data: {
            payroll: null,
            billing: null,
            columns: []
        },
        message: ''
    })


    let updateBillingDetails = async (updates) => {

        console.log('updating billing details with updates', updates)

        try {
            //vat from calculation
            let vat_from = updates.vat_from || payrollAndBilling.data.billing?.vat_from || "admin_cost"

            let updateResult = await updateBillingDetailsById(id, updates)
            let currentBillingDetails = {
                ...payrollAndBilling.data.billing,
                ...updateResult,
                rebate: updateResult.rebate ?? payrollAndBilling.data.billing?.rebate ?? 0,
            }
            let updatedBilling = BillingFigureCalculatorService.computeBillingFigures(currentBillingDetails, vat_from)
            BillingUtilities.generateOtherDetailsBilling(updatedBilling)

            console.log('update billing details result', updateResult)

            console.log('updated billing details', updatedBilling)

            let newBillingDetails = {
                ...payrollAndBilling.data.billing,
                net_billing: updatedBilling.net_billing,
                gross_billing: updatedBilling.gross_billing,
                admin_cost: updatedBilling.admin_cost,
                rebate: updatedBilling.rebate,
                vat: updatedBilling.vat,
                withholding_tax: updatedBilling.withholding_tax,
                salary_deduction: updatedBilling.salary_deduction,
                meal_allowance: updatedBilling.meal_allowance,
                other_deductions: updatedBilling.other_deductions,
                soa_number: updateResult.soa_number,
                billed_to: updateResult.billed_to,
                vat_from: updateResult.vat_from,
                rebate_group_id: updateResult.rebate_group_id,
                rebate_group_name: updateResult.rebate_group_name,
                rebate_group_amount: updateResult.rebate_group_amount,
                rebate_overridden: updateResult.rebate_overridden,
                rebate_overridden_by: updateResult.rebate_overridden_by,
                rebate_overridden_at: updateResult.rebate_overridden_at,
            }
            console.log('new billing details would be', newBillingDetails)

            setPayrollAndBilling((payrollAndBilling) => ({
                ...payrollAndBilling,
                data: {
                    ...payrollAndBilling.data,
                    billing: newBillingDetails
                }
            }))

            Swal.fire({
                title: 'Updated',
                icon: 'success',
                text: 'Billing details updated',
            })
        } catch (err) {
            console.log(err)
            Swal.fire({
                title: 'Error!',
                text: 'Cannot update billing details today, please contact devs for assistance.',
                icon: 'error',
                confirmButtonText: 'Cool'
            })
        }



    }

    //main function to fetch payroll and billing information
    const fetchPayrollAndBillingInfo = useCallback(async () => {
        console.log('fetching')
        setPayrollAndBilling((prev) => ({
            ...prev, status: 'loading'
        }))


        try {


            let { billing, payroll } = await BillingInformationService.getById(id)
            if (!billing) {
                throw new Error('No billing details returned for this payroll')
            }

            if (!payroll) {
                throw new Error('No payroll returned for this billing request')
            }

            /*
        const sampleAllowances = [
            {
            _id: "1",
            payroll_info_id: "68be902d0cc36bf718adf7ae",
            allowance_type: "transport",
            amount: 20,
            description: "Transport allowance",
            is_taxable: false,
            createdAt: "2025-09-15T06:55:23.606Z",
            updatedAt: "2026-02-04T03:09:53.118Z",
            __v: 0,
            source: "personnel",
            user_id: "692660e6cdadfe00693d0d63",
            name: "Transport",
            type: "billing"
            },
            {
            _id: "2",
            payroll_info_id: "68be902d0cc36bf718adf7ae",
            allowance_type: "meal",
            amount: 15,
            description: "Meal allowance",
            is_taxable: true,
            createdAt: "2025-09-15T06:55:23.606Z",
            updatedAt: "2026-02-04T03:09:53.118Z",
            __v: 0,
            source: "store",
            name: "Meal",
            type: "billing"
            },
        ];*/

            const otherDeductionColumns = buildOtherDeductionColumns(payroll.informations || [])
            payroll.informations = (payroll.informations || []).map((entry) => ({
                ...entry,
                ...flattenOtherDeductionBreakdown(entry, otherDeductionColumns),
            }))
            payroll.informations = ColumnsGeneratorService.addTotalColumnForPayrollEntries(payroll.informations || [])
            payroll.informations = ColumnsGeneratorService.addTotalDeductionsForPayrollEntries(payroll.informations || [], payroll.allowances || [])
            payroll.informations = ColumnsGeneratorService.addGrossBillingForPayrollEntries(payroll.informations || [], payroll.allowances || [])

            billing.gross_billing = BillingFigureCalculatorService.generateGrossBillingForThisCutoff(payroll.informations || [])

            //computes allowances for this cutoff
            /**
             * 
             * {
             *   chargedToClient: 0,
             *   lessToBilling: 0,
             *   gross: 0
             * }
             */
            let computedAllowances = AllowanceService.computeAllowanceToBeDeducted(payroll.informations || [], payroll.allowances || [])
            // console.log('computed allowances', computedAllowances)
            //compute initial billing figures
            //console.log('initial billing', billing, billing.vat_from)
            let billingWithFigures = BillingFigureCalculatorService.computeBillingFigures(billing, billing.vat_from || "admin_cost")
            console.log('billing with figures', billingWithFigures.net_billing)

            //let billingAppliedWithAllowances = AllowanceService.applyAllowancesToBillingDetails(billingWithFigures, computedAllowances)

            let updatedBilling = AllowanceService.applyAllowancesToBillingDetails(billingWithFigures, computedAllowances)
            console.log('updated billing', updatedBilling.net_billing, {...updatedBilling})

            //recompute billing figures after applying allowances
            //updatedBilling = BillingFigureCalculatorService.computeBillingFigures(updatedBilling, updatedBilling.vat_from || "admin_cost")
            let finalBilling = BillingUtilities.generateOtherDetailsBilling(updatedBilling)

            console.log('final billing after applying allowances', finalBilling.net_billing, {...finalBilling})


            const billingPayrollOpts = { consolidatePersonnelAllowances: true }
            let payrollEntriesWithAllowances = AllowanceService.addAllowancesToPayrollEntries(
                payroll.informations || [],
                payroll.allowances || [], otherDeductionColumns,
                billingPayrollOpts
            )
            const hasAppliedPersonnelAllowances = payrollEntriesWithAllowances.some(
                (e) => Number(e?.['Personnel allowances'] || 0) !== 0
            )
            let payrollColumns = ColumnsGeneratorService.generateColumnsForPayroll(
                payrollEntriesWithAllowances,
                payroll.allowances || [],
                otherDeductionColumns,
                {
                    ...billingPayrollOpts,
                    showPersonnelAllowancesColumn: hasAppliedPersonnelAllowances,
                }
            )

            payrollEntriesWithAllowances = BillingUtilities.addIndexesToPayrollEntries(payrollEntriesWithAllowances)

            let itemizedAllowances = AllowanceService.itemizeAndComputeAllowances(payroll.informations || [], payroll.allowances || [])
            itemizedAllowances = AllowanceService.consolidatePersonnelAllowancesForBilling(itemizedAllowances)
            console.log('itemized allowances', itemizedAllowances)
            payroll = {
                ...payroll,
                informations: payrollEntriesWithAllowances,
                columns: payrollColumns,
                otherDeductionColumns,
                itemizedAllowances: itemizedAllowances
            }

            billing = {...finalBilling}


            console.log('setting payroll and billing information', payroll, billing)
            setPayrollAndBilling((prev) => ({
                ...prev, status: 'success',
                data: {
                    payroll: payroll,
                    billing: billing,
                    columns: payrollColumns
                }
            }))

        } catch (err) {
            console.log('err', err)
            setPayrollAndBilling((prev) => ({
                ...prev,
                status: 'error',
                data: {
                    payroll: null,
                    billing: null,
                    columns: []
                },
                message: err?.message || "Error in fetching payroll and billing information"
            }))
        }
    }, [id])

    useEffect(() => {
        fetchPayrollAndBillingInfo()
    }, [fetchPayrollAndBillingInfo])

    const setNewBillingDetails = async (updates) => {
        setPayrollAndBilling((prev) => ({
            ...prev,
            data: {
                ...prev.data,
                billing: {
                    ...prev.data.billing,
                    ...updates
                }
            }
        }))
    }

    const setPayslipHiddenAllowanceColumnKeys = useCallback((keys) => {
        setPayrollAndBilling((prev) => ({
            ...prev,
            data: {
                ...prev.data,
                payroll: prev.data.payroll
                    ? {
                          ...prev.data.payroll,
                          payslip_hidden_allowance_column_keys: keys,
                      }
                    : null,
            },
        }))
    }, [])

    return {
        payroll: payrollAndBilling.data?.payroll,
        billingDetails: payrollAndBilling.data?.billing,
        status: payrollAndBilling.status,
        payrollColumns: payrollAndBilling.data?.columns || [],
        updateBillingDetails,
        setNewBillingDetails,
        refetchPayrollBilling: fetchPayrollAndBillingInfo,
        setPayslipHiddenAllowanceColumnKeys,
    }
}


export default usePayroll
